import { clearGtmUserData, pushGtmUserData } from './user-data'
import { normalizeAndHashUserData, type UserDataInput } from './user-data-hash'

/**
 * Retract a reported identity — on logout, or when the visitor changes. Without
 * it the previous visitor keeps being reported for the life of the document, and
 * on a shared device that attributes one person's activity to another.
 *
 * Resolves whether the retraction landed, because a caller that treats a failed
 * one as done forgets it still owes it — and the identity it meant to clear is
 * still on the queue.
 */
export function retractUserData(): 'retracted' | 'failed' {
    try {
        clearGtmUserData()
        return 'retracted'
    } catch {
        return 'failed'
    }
}

export type UserDataOutcome = 'reported' | 'unmatchable' | 'superseded' | 'failed'

interface ReportOptions {
    /**
     * Consulted once the digest resolves, immediately before the push. A producer
     * whose identity can change while a digest is in flight passes its own
     * liveness here: two reports started close together are not ordered by
     * anything, and the queue model merges recursively, so a payload that resolves
     * last is the one the container reads.
     */
    isSuperseded?: () => boolean
}

/**
 * Report a visitor's identity as the Google Ads Enhanced Conversions match
 * signal: normalize, hash, and push. Every producer goes through here, so the
 * shape on the queue cannot drift between surfaces.
 *
 * **Never rejects.** An address that does not normalize is `unmatchable` rather
 * than a failure — a visitor we cannot match is an ordinary outcome, not
 * something to log; a hashing or container-side throw resolves `failed`. Losing
 * a match signal must not take a signup or a page render with it, and the
 * producer decides what a lost one is worth saying.
 */
export async function reportUserData(
    input: UserDataInput,
    { isSuperseded }: ReportOptions = {},
): Promise<UserDataOutcome> {
    try {
        const userData = await normalizeAndHashUserData(input)
        if (!userData) {
            return 'unmatchable'
        }
        if (isSuperseded?.()) {
            return 'superseded'
        }
        pushGtmUserData(userData)
        return 'reported'
    } catch {
        return 'failed'
    }
}
